import { FC, MouseEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Col, Row } from 'antd';

import { AppDispatch } from 'app/store';

import AttachmentsComponent from 'components/AttachmentsComponent/AttachmentsComponent';
import AvailabilityStatus from 'components/AvailabilityStatus/AvailabilityStatus';
import Contacts from 'components/Contacts/Contacts';
import CountryPreview from 'components/CountryPreview';
import EducationWorkExperience from 'components/EducationWorkExperience/EducationWorkExperience';
import JobseekerAvatar from 'components/JobseekerAvatar/JobseekerAvatar';
import LanguageWithLevel from 'components/Jobseekers/components/LanguageWithLevel/LanguageWithLevel';
import Tags from 'components/Jobseekers/components/Tags';
import LocationDisplay from 'components/LocationDisplay/LocationDisplay';
import SidebarNavigation from 'components/SidebarNavigation';
import SkillMatch from 'components/SkillMatch/SkillMatch';
import StageSelector from 'components/StageSelector/StageSelector';
import * as applicationConfig from 'components/StageSelector/clientApplicationConfig';
import StatusIcon from 'components/StatusIcon/StatusIcon';
import StatusLongTag from 'components/StatusLongTag/StatusLongTag';

import {
  selectActiveApplication,
  selectApplications
} from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';
import { selectDictionary } from 'features/dictionaries/dictionariesSelectors';
import jobseekersService from 'features/jobseekers/jobseekersService';
import { selectActiveUserId } from 'features/sidebar/sidebarSelectors';
import {
  closeSidebar,
  setActiveUserId,
  setSidebarContent
} from 'features/sidebar/sidebarSlice';

import useErrorCatch from 'hooks/useErrorCatch';
import usePageviewEvent from 'hooks/usePageViewEvent';
import { useSyncApplicationRoute } from 'hooks/useSyncApplicationRoute';

import { ApplicationWithOfferLetter } from 'types/applications';
import { Jobseeker } from 'types/jobseekers';

import {
  Dictionaries,
  GTMEvents,
  JobseekerAttachmentTypes,
  OfferLetterStatus,
  SidebarContentTypes
} from 'constants/enums';
import { commonFileTypes } from 'constants/jobseekerSettings';
import { STATUS_UPDATED_BY_JOBLIO } from 'constants/notifications';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import { getCountryCodeFromApplication } from '../utils/getCountryCodeFromApplication';
import styles from './ApplicationView.module.scss';

interface ApplicationViewProps {
  isLoading: boolean;
}

const ApplicationView: FC<ApplicationViewProps> = ({ isLoading }) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const applicationId = useSelector(selectActiveUserId);
  const application = useSelector(selectActiveApplication);
  const applications = useSelector(selectApplications);
  const [isJobseekerLoading, setIsJobseekerLoading] = useState<boolean>(false);
  const [jobseeker, setJobSeeker] = useState<Jobseeker | null>(null);
  const [isOfferLetterLoading, setIsOfferLetterLoading] = useState(false);
  const [applicationWithOfferLetter, setApplicationWithOfferLetter] =
    useState<ApplicationWithOfferLetter | null>(null);

  const clientApplicationStages = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClient)
  );
  const clientApplicationTags = useSelector(
    selectDictionary(Dictionaries.ApplicationStageClientTag)
  );
  const countriesDictionary = useSelector(
    selectDictionary(Dictionaries.ClientCountry)
  );
  const skillsets = useSelector(selectDictionary(Dictionaries.Skillset));
  const jobShiftOptions = useSelector(selectDictionary(Dictionaries.JobShift));
  const jobTypeOptions = useSelector(selectDictionary(Dictionaries.JobType));

  useSyncApplicationRoute(applicationId);
  usePageviewEvent(GTMEvents.ApplicationsListViewSidebar);

  const fetchJobseekerData = async (jobseekerId: string): Promise<void> => {
    try {
      setIsJobseekerLoading(true);
      const jobseeker = await jobseekersService.getJobseekerById(jobseekerId);
      setJobSeeker(jobseeker);
    } catch (error) {
      catchError({
        error,
        default: {
          message: t('error.message.operationFailed'),
          description: t('error.message.fetchJobseekerData')
        }
      });
      dispatch(closeSidebar());
    } finally {
      setIsJobseekerLoading(false);
    }
  };

  const fetchApplicationWithOfferLetter = async (applicationId: string) => {
    setIsOfferLetterLoading(true);
    try {
      const applicationWithOfferLetter =
        await applicationsService.getApplicationWithOfferLetter(applicationId);
      setApplicationWithOfferLetter(applicationWithOfferLetter);
    } catch (error) {
      setApplicationWithOfferLetter(null);
    } finally {
      setIsOfferLetterLoading(false);
    }
  };

  const handleJobClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();

      dispatch(
        setSidebarContent({
          contentId: SidebarContentTypes.ClientJobView,
          activeUserId: application?.jobId
        })
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!application?.jobseekerId) {
      return;
    }
    fetchJobseekerData(application.jobseekerId);
  }, [application?.jobseekerId]);

  useEffect(() => {
    if (application?.id && application?.offerId) {
      fetchApplicationWithOfferLetter(application?.id);
    }
  }, [application?.id, application?.offerId]);

  const isDisplayLoading =
    isJobseekerLoading || isOfferLetterLoading || isLoading || !application;

  const isCVAttachment = application?.attachments?.some(
    (attachment) => attachment.type === JobseekerAttachmentTypes.CV
  );

  const jobseekerPhotoUrlEndpoint = jobseeker?.attachments?.find(
    (attachment) => attachment.type === JobseekerAttachmentTypes.JobseekerPhoto
  )?.url;

  const isShowScreeningInfoRow =
    jobseeker?.hasDriverLicense ||
    application?.relocating ||
    application?.commuting ||
    application?.workAuthorization;

  const isShowContactsSection =
    application?.jobseekerEmail || application?.jobseekerPhone;

  return (
    <aside>
      <SidebarNavigation
        id={applicationId}
        data={applications}
        action={setActiveUserId}
      />
      {isDisplayLoading ? (
        <div>{t('common.loading')}</div>
      ) : (
        <>
          <div className={styles.commonInfoWrapper}>
            <JobseekerAvatar
              photoUrlEndpoint={jobseekerPhotoUrlEndpoint ?? null}
            />

            <div className={styles.nameDataWrapper}>
              <div className={styles.markAndTagWrapper}>
                <SkillMatch matchData={application.matchData} />
              </div>
              <div className={styles.title}>
                {application.jobseekerFullName}
              </div>
              <div className={styles.availabilityStatusWrapper}>
                <AvailabilityStatus
                  status={jobseeker?.availabilityStatus}
                  tooltipTitle={t(STATUS_UPDATED_BY_JOBLIO)}
                />
              </div>
            </div>
          </div>

          <Row className={styles.row}>
            <Col span={12}>
              <StageSelector
                value={application.clientStage}
                id={application.id}
                options={clientApplicationStages}
                hasPermission
                config={applicationConfig}
                dictionary={Dictionaries.ApplicationClientStageFlow}
              />
              <div className={styles.label}>
                {t('sidebar.application.view.applicationStatus')}
              </div>
            </Col>
            {application.tags && (
              <Col span={12}>
                <Tags
                  dictionary={clientApplicationTags}
                  values={application.tags}
                />
              </Col>
            )}
          </Row>

          <Row className={styles.row}>
            <Col span={24}>
              <div className={styles.jobName} onClick={handleJobClick}>
                {application.jobName}
              </div>
              <div className={styles.label}>
                {t('sidebar.application.view.jobApplied')}
              </div>
            </Col>
          </Row>

          {isShowContactsSection && (
            <>
              <Row className={styles.row}>
                <Col span={24}>
                  <div className={styles.sectionHeader}>
                    {t('sidebar.application.view.contactInfo')}
                  </div>
                </Col>
                <Col span={24}>
                  <div className={styles.divider} />
                </Col>
              </Row>

              <div className={styles.sectionContentWrapper}>
                <Row className={styles.row}>
                  <Col span={24}>
                    <Contacts
                      phone={application?.jobseekerPhone}
                      email={application?.jobseekerEmail}
                    />
                  </Col>
                </Row>
              </div>
            </>
          )}

          <div className={styles.sectionContentWrapper}>
            <Row className={styles.row}>
              <Col span={24}>
                <div className={styles.sectionHeader}>
                  {t('sidebar.application.view.resume')}
                </div>
              </Col>
              <Col span={24}>
                <div className={styles.divider} />
              </Col>
            </Row>

            <Row className={styles.row}>
              <Col span={24}>
                {isCVAttachment ? (
                  <AttachmentsComponent
                    attachmentType={JobseekerAttachmentTypes.CV}
                    attachmentList={application?.attachments || []}
                    onChange={() => {}}
                    allowedFileTypes={commonFileTypes}
                    isDeleteDisabled
                    isPreviewFile
                  />
                ) : (
                  <div className={styles.fileUnavailable}>
                    {t('sidebar.application.view.resumeNotFound')}
                  </div>
                )}
              </Col>
            </Row>
          </div>

          <Row className={styles.row}>
            <Col span={24}>
              <div className={styles.sectionHeader}>
                {t('sidebar.application.view.bio')}
              </div>
            </Col>
            <Col span={24}>
              <div className={styles.divider} />
            </Col>
          </Row>

          <div className={styles.sectionContentWrapper}>
            <Row className={styles.row}>
              <Col span={24}>
                {application?.jobLocation ? (
                  <LocationDisplay
                    location={application?.jobLocation}
                    countryCode={getCountryCodeFromApplication(
                      application.jobLocation.country,
                      countriesDictionary
                    )}
                  />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>
                  {t('sidebar.application.view.jobLocation')}
                </div>
              </Col>
            </Row>

            <Row className={styles.row}>
              <Col span={24}>
                {jobseeker?.citizenship ? (
                  <CountryPreview
                    countryCode={jobseeker.citizenship.code}
                    countryName={jobseeker.citizenship.name}
                  />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>
                  {t('sidebar.application.view.citizenship')}
                </div>
              </Col>
            </Row>

            <Row className={styles.row}>
              <Col span={24}>
                {jobseeker?.skillsets ? (
                  <Tags dictionary={skillsets} values={jobseeker.skillsets} />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>
                  {t('sidebar.application.view.skillset')}
                </div>
              </Col>
            </Row>

            <Row className={styles.row}>
              <Col span={24}>
                {jobseeker?.languages ? (
                  <LanguageWithLevel langs={jobseeker.languages} />
                ) : (
                  FALLBACK_VALUE
                )}
                <div className={styles.label}>
                  {t('sidebar.application.view.languages')}
                </div>
              </Col>
            </Row>
          </div>

          <div className={styles.sectionContentWrapper}>
            <EducationWorkExperience jobseekerId={jobseeker?.id} />
          </div>

          <Row className={styles.row}>
            <Col span={24}>
              <div className={styles.sectionHeader}>
                {t('sidebar.application.view.screeningInfo')}
              </div>
            </Col>
            <Col span={24}>
              <div className={styles.divider} />
            </Col>
          </Row>

          <div className={styles.sectionContentWrapper}>
            <Row className={styles.row}>
              <Col span={12}>
                <Tags
                  dictionary={jobTypeOptions}
                  values={jobseeker?.schedule}
                  isTextMode
                />
                <div className={styles.label}>
                  {t('sidebar.application.view.preferredContactType')}
                </div>
              </Col>

              <Col span={12}>
                <Tags
                  dictionary={jobShiftOptions}
                  values={jobseeker?.preferredSchedule}
                  isTextMode
                />
                <div className={styles.label}>
                  {t('sidebar.application.view.preferredShift')}
                </div>
              </Col>
            </Row>

            {jobseeker?.weekendOvertime && (
              <Row className={styles.row}>
                <Col span={12} className={styles.status}>
                  <StatusIcon status />
                  <div className={styles.label}>
                    {t('sidebar.application.view.weekendAvailability')}
                  </div>
                </Col>
                <Col span={12} className={styles.status}>
                  <StatusIcon status />
                  <div className={styles.label}>
                    {t('sidebar.application.view.overtimeAvailability')}
                  </div>
                </Col>
              </Row>
            )}

            {isShowScreeningInfoRow && (
              <Row className={styles.screeningRow}>
                {jobseeker?.hasDriverLicense && (
                  <Col span={12} className={styles.screeningStatus}>
                    <StatusIcon status />
                    <div className={styles.label}>
                      {t('sidebar.application.view.driversLicense')}
                    </div>
                  </Col>
                )}

                {application?.relocating && (
                  <Col span={12} className={styles.screeningStatus}>
                    <StatusIcon status />
                    <div className={styles.label}>
                      {t('sidebar.application.view.openToRelocate')}
                    </div>
                  </Col>
                )}

                {application?.commuting && (
                  <Col span={12} className={styles.screeningStatus}>
                    <StatusIcon status />
                    <div className={styles.label}>
                      {t('sidebar.application.view.openToCommute')}
                    </div>
                  </Col>
                )}

                {application?.workAuthorization && (
                  <Col span={12} className={styles.screeningStatus}>
                    <StatusIcon status />
                    <div className={styles.label}>
                      {t('sidebar.application.view.workAuthorization')}
                    </div>
                  </Col>
                )}
              </Row>
            )}

            <Row className={styles.row}>
              <Col span={24}>
                <StatusLongTag status={jobseeker?.felonyConviction} />
                <div className={styles.label}>
                  {t('sidebar.application.view.felonyConviction')}
                </div>
              </Col>
              <Col span={24}>
                <StatusLongTag status={jobseeker?.backgroundCheckConsent} />
                <div className={styles.label}>
                  {t('sidebar.job.backgroundCheckConsent')}
                </div>
              </Col>
            </Row>
          </div>

          <Row className={styles.row}>
            <Col span={24}>
              <div className={styles.sectionHeader}>
                {t('sidebar.application.view.calendarEvents')}
              </div>
            </Col>
            <Col span={24}>
              <div className={styles.divider} />
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={12}>
              {application?.clientInterviewDate || FALLBACK_VALUE}
              <div className={styles.label}>
                {t('sidebar.application.view.interviewScheduled')}
              </div>
            </Col>

            <Col span={12}>
              {applicationWithOfferLetter?.createdDate || FALLBACK_VALUE}
              <div className={styles.label}>
                {t('sidebar.application.view.offerSent')}
              </div>
            </Col>
          </Row>

          <Row className={styles.row}>
            <Col span={12}>
              {applicationWithOfferLetter?.editDate &&
              applicationWithOfferLetter.status === OfferLetterStatus.Accepted
                ? applicationWithOfferLetter?.editDate
                : FALLBACK_VALUE}
              <div className={styles.label}>
                {t('sidebar.application.view.offerSigned')}
              </div>
            </Col>
          </Row>
        </>
      )}
    </aside>
  );
};

export default ApplicationView;
